import { useRef, useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DottedMap from 'dotted-map';

/* ── WorldMap component ─────────────────────────────────────────────────── */
export function WorldMap({
  dots = [],
  lineColor = '#6366f1',
  animationDuration = 2,
  loop = true,
}) {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [inView, setInView] = useState(false);

  /* Pause animations when out of viewport */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const map = useMemo(() => new DottedMap({ height: 100, grid: 'diagonal' }), []);

  const svgMap = useMemo(() =>
    map.getSVG({
      radius: 0.22,
      color: '#ffffff18',
      shape: 'circle',
      backgroundColor: 'transparent',
    }), [map]);

  const projectPoint = (lat, lng) => ({
    x: (lng + 180) * (800 / 360),
    y: (90 - lat) * (400 / 180),
  });

  const createCurvedPath = (start, end) => {
    const midX = (start.x + end.x) / 2;
    const midY = Math.min(start.y, end.y) - 50;
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  const staggerDelay = 0.3;
  const totalAnimTime = dots.length * staggerDelay + animationDuration;
  const pauseTime = 2;
  const fullCycle = totalAnimTime + pauseTime;

  return (
    <div ref={wrapRef} style={{ width: '100%', position: 'relative', aspectRatio: '2/1', overflow: 'hidden', borderRadius: '12px' }}>
      {/* Dotted base map */}
      <img
        src={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
        alt="world map"
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          position: 'absolute', inset: 0,
          maskImage: 'linear-gradient(to bottom, transparent, white 12%, white 88%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, white 12%, white 88%, transparent)',
          pointerEvents: 'none', userSelect: 'none',
        }}
        draggable={false}
      />

      {/* SVG overlay for paths + points */}
      <svg
        ref={svgRef}
        viewBox="0 0 800 400"
        style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="wm-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="white"    stopOpacity="0" />
            <stop offset="5%"   stopColor={lineColor} stopOpacity="1" />
            <stop offset="95%"  stopColor={lineColor} stopOpacity="1" />
            <stop offset="100%" stopColor="white"    stopOpacity="0" />
          </linearGradient>
          <filter id="wm-glow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Paths */}
        {dots.map((dot, i) => {
          const s = projectPoint(dot.start.lat, dot.start.lng);
          const e = projectPoint(dot.end.lat, dot.end.lng);
          const path = createCurvedPath(s, e);
          const t0 = (i * staggerDelay) / fullCycle;
          const t1 = (i * staggerDelay + animationDuration) / fullCycle;
          const tR = totalAnimTime / fullCycle;

          return (
            <g key={`p${i}`}>
              <motion.path
                d={path} fill="none"
                stroke="url(#wm-grad)" strokeWidth="1.2"
                initial={{ pathLength: 0 }}
                animate={inView && loop ? { pathLength: [0, 0, 1, 1, 0] } : { pathLength: 0 }}
                transition={inView && loop ? {
                  duration: fullCycle, times: [0, t0, t1, tR, 1],
                  ease: 'easeInOut', repeat: Infinity,
                } : { duration: 0 }}
              />
              {inView && loop && (
                <motion.circle r="3.5" fill={lineColor}
                  initial={{ offsetDistance: '0%', opacity: 0 }}
                  animate={{ offsetDistance: [null, '0%', '100%', '100%', '100%'], opacity: [0, 0, 1, 0, 0] }}
                  transition={{ duration: fullCycle, times: [0, t0, t1, tR, 1], ease: 'easeInOut', repeat: Infinity }}
                  style={{ offsetPath: `path('${path}')`, filter: `drop-shadow(0 0 4px ${lineColor})` }}
                />
              )}
            </g>
          );
        })}

        {/* Points */}
        {dots.map((dot, i) => {
          const s = projectPoint(dot.start.lat, dot.start.lng);
          const e = projectPoint(dot.end.lat, dot.end.lng);
          return (
            <g key={`pt${i}`}>
              {[{ pt: s, label: dot.start.label, delay: 0 }, { pt: e, label: dot.end.label, delay: 0.5 }].map(({ pt, label, delay }, j) => (
                <g key={j}>
                  <motion.g
                    onHoverStart={() => label && setHovered(label)}
                    onHoverEnd={() => setHovered(null)}
                    whileHover={{ scale: 1.3 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle cx={pt.x} cy={pt.y} r="3" fill={lineColor} filter="url(#wm-glow)" />
                    <circle cx={pt.x} cy={pt.y} r="3" fill={lineColor} opacity="0.4">
                      <animate attributeName="r" from="3" to="11" dur="2s" begin={`${delay}s`} repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.5" to="0" dur="2s" begin={`${delay}s`} repeatCount="indefinite" />
                    </circle>
                  </motion.g>
                  {label && (
                    <motion.g
                      initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 * i + 0.3, duration: 0.5 }}
                      style={{ pointerEvents: 'none' }}
                    >
                      <foreignObject x={pt.x - 45} y={pt.y - 32} width="90" height="24">
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
                        }}>
                          <span style={{
                            fontSize: '0.6rem', fontWeight: 600, fontFamily: "'Space Mono', monospace",
                            padding: '2px 7px', borderRadius: '6px',
                            background: 'rgba(10,10,16,0.88)',
                            color: '#fff', border: '1px solid rgba(255,255,255,0.12)',
                            letterSpacing: '0.05em', textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                          }}>{label}</span>
                        </div>
                      </foreignObject>
                    </motion.g>
                  )}
                </g>
              ))}
            </g>
          );
        })}
      </svg>

      {/* Hover tooltip mobile */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            style={{
              position: 'absolute', bottom: '1rem', left: '1rem',
              background: 'rgba(10,10,16,0.9)', color: '#fff',
              padding: '6px 14px', borderRadius: '8px',
              fontSize: '0.75rem', fontWeight: 600,
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)',
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {hovered}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
